import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  User, MessageSquare, Utensils, Dumbbell,
  Camera, BarChart2, ArrowLeft, ArrowRight,
  Search, Copy, Check
} from 'lucide-react';
import { useClient } from '../../context/ClientContext';
import { useTheme } from '../../context/ThemeContext';
import ClientAvatar from '../admin/ClientAvatar';
import ClientSwitcherModal from './ClientSwitcherModal';

export default function ClientWorkspaceHeader() {
  const { selectedClient } = useClient();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const codeRef = useRef(null);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'english');
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  // Global keyboard shortcut for client switcher (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSwitcherOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const t = (en, he) => (language === 'hebrew' ? he : en);

  // Quick action: copy the client's user code, with inline "Copied" feedback.
  const copyUserCode = async () => {
    const code = String(selectedClient?.user_code || '').trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCodeCopied(false), 1500);
    } catch {
      // Clipboard blocked (e.g. insecure context): select the code so it can be copied by hand.
      const node = codeRef.current;
      if (node && window.getSelection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  if (!selectedClient) {
    return null;
  }

  const clientName = selectedClient.full_name || selectedClient.name || selectedClient.user_code || t('Unnamed Client', 'לקוח');

  const tabs = [
    {
      id: 'overview',
      path: '/client-dashboard',
      matchPaths: ['/client-dashboard'],
      label: t('Overview', 'סקירה'),
      icon: User,
      tourId: 'playbook-workspace-tab-overview',
    },
    {
      id: 'chat',
      path: '/chat',
      matchPaths: ['/chat'],
      label: t('Chat', 'צ\'אט'),
      icon: MessageSquare,
      tourId: 'playbook-workspace-tab-chat',
    },
    {
      id: 'nutrition',
      path: '/client-meal-plans',
      matchPaths: ['/client-meal-plans', '/create-meal-plan'],
      label: t('Nutrition Plan', 'תוכנית תזונה'),
      icon: Utensils,
      tourId: 'playbook-topbar-meal-plans',
    },
    {
      id: 'training',
      path: '/training-management',
      matchPaths: ['/training-management', '/training-management/builder'],
      label: t('Training Plan', 'תוכנית אימון'),
      icon: Dumbbell,
      tourId: 'playbook-topbar-training',
    },
    {
      id: 'food-logs',
      path: '/client-food-logs',
      matchPaths: ['/client-food-logs'],
      label: t('Food Logs', 'יומן אכילה'),
      icon: Camera,
      tourId: 'playbook-workspace-tab-foodlogs',
    },
    {
      id: 'analytics',
      path: '/analytics',
      matchPaths: ['/analytics'],
      label: t('Analytics', 'אנליטיקה'),
      icon: BarChart2,
      tourId: 'playbook-topbar-analytics',
    },
  ];

  const isTabActive = (tab) => {
    return tab.matchPaths.some((p) => location.pathname.startsWith(p));
  };

  // One slim row: who (name + code), where (tabs), and the switcher. Phone and email sit in the name's tooltip;
  // they're on Overview in full. Below lg the tabs wrap to a second row.
  const contactLine = [selectedClient.phone_number, selectedClient.email].filter(Boolean).join(' · ');

  return (
    <>
      <section
        data-tour="playbook-client-workspace-header"
        aria-label={t('Client Workspace', 'מרחב עבודה לקוח')}
        className="w-full border-b border-rule bg-canvas/70 dark:bg-canvas-dark/70 transition-colors"
        dir={direction}
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 px-4 sm:px-6 lg:flex-nowrap lg:px-8">
          {/* Identity: back to the practice, avatar, name, code. */}
          <div className="flex min-w-0 items-center gap-2 py-1.5">
            <Link
              to="/dietitian-dashboard"
              data-tour="playbook-workspace-back"
              className="-ms-2 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-canvas hover:text-ink dark:hover:bg-surface-dark dark:hover:text-white transition-colors"
              title={t('Back to Practice Overview', 'חזרה לסקירה כללית')}
              aria-label={t('Back to Practice Overview', 'חזרה לסקירה כללית')}
            >
              {direction === 'rtl' ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
            </Link>
            <div className="shrink-0">
              <ClientAvatar client={selectedClient} size="sm" />
            </div>
            <h1 className="max-w-[14rem] truncate text-sm font-bold text-ink dark:text-white" title={contactLine || undefined}>
              {clientName}
            </h1>
            {selectedClient.engagementPulse === 'healthy' ? (
              <span className="hidden shrink-0 items-center rounded-full border border-forest/20 bg-forest-faint px-2 py-0.5 text-[11px] font-semibold text-forest dark:bg-forest/20 dark:text-forest-ondark sm:inline-flex">
                {t('Active in 24h', 'פעיל/ה ב-24 שעות')}
              </span>
            ) : selectedClient.engagementPulse ? (
              <span className="hidden shrink-0 items-center rounded-full border border-rule bg-canvas px-2 py-0.5 text-[11px] font-medium text-ink-muted dark:bg-surface-dark dark:text-ink-muted-dark sm:inline-flex">
                {selectedClient.engagementPulse}
              </span>
            ) : null}
            {selectedClient.user_code && (
              <span className="hidden shrink-0 items-center rounded border border-rule bg-surface dark:bg-surface-dark md:inline-flex">
                <span ref={codeRef} className="select-all py-0.5 pe-1 ps-1.5 font-mono text-[11px] text-ink-muted dark:text-ink-muted-dark">
                  {selectedClient.user_code}
                </span>
                <button
                  type="button"
                  onClick={copyUserCode}
                  aria-label={codeCopied ? t('Copied', 'הועתק') : t('Copy user code', 'העתקת קוד משתמש')}
                  title={codeCopied ? t('Copied', 'הועתק') : t('Copy user code', 'העתקת קוד משתמש')}
                  className={`inline-flex h-6 items-center gap-1 rounded-e border-s border-rule px-1.5 text-[11px] font-medium outline-none transition-colors duration-120 focus-visible:ring-2 focus-visible:ring-forest ${
                    codeCopied
                      ? 'text-forest-ondark dark:text-forest'
                      : 'text-ink-muted hover:bg-moss hover:text-ink dark:text-ink-muted-dark dark:hover:bg-moss-dark dark:hover:text-ink-dark'
                  }`}
                >
                  {codeCopied ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
                </button>
                <span className="sr-only" aria-live="polite">{codeCopied ? t('User code copied', 'קוד המשתמש הועתק') : ''}</span>
              </span>
            )}
          </div>

          {/* Tabs: inline on wide screens, their own row below lg. */}
          <nav
            data-tour="playbook-workspace-tabs"
            role="tablist"
            aria-label={t('Client Navigation', 'ניווט לקוח')}
            className="no-scrollbar order-last -mb-px flex w-full items-center gap-0.5 overflow-x-auto lg:order-none lg:w-auto lg:flex-1 lg:justify-center"
          >
            {tabs.map((tab) => {
              const active = isTabActive(tab);
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  type="button"
                  data-tour={tab.tourId}
                  aria-selected={active}
                  onClick={() => navigate(tab.path)}
                  className={`inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-[13px] font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest ${
                    active
                      ? 'border-forest text-forest dark:text-forest-ondark'
                      : 'border-transparent text-ink-muted hover:border-rule hover:text-ink dark:text-ink-muted-dark dark:hover:text-white'
                  }`}
                >
                  <Icon size={15} className={active ? 'text-forest dark:text-forest-ondark' : 'opacity-70'} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Switch client: a compact button; ⌘K works anywhere. */}
          <button
            type="button"
            data-tour="playbook-topbar-client-btn"
            onClick={() => setIsSwitcherOpen(true)}
            className={`ms-auto inline-flex h-9 shrink-0 items-center gap-2 rounded-full border px-3 text-[13px] font-semibold transition-colors duration-200 lg:ms-0 ${
              isDarkMode
                ? 'border-line-dark bg-surface-dark text-white hover:border-forest'
                : 'border-line bg-surface text-ink hover:border-forest'
            }`}
            aria-label={t('Switch Client', 'החלפת לקוח')}
          >
            <Search size={14} className="shrink-0 text-forest" />
            <span className="hidden sm:inline">{t('Switch Client', 'החלף לקוח')}</span>
            <kbd className="hidden items-center rounded border border-rule bg-canvas px-1.5 py-0.5 font-mono text-[10px] text-ink-muted dark:bg-canvas-dark dark:text-ink-muted-dark xl:inline-flex">
              ⌘K
            </kbd>
          </button>
        </div>
      </section>

      {/* Quick Switcher Modal (⌘K) */}
      <ClientSwitcherModal
        isOpen={isSwitcherOpen}
        onClose={() => setIsSwitcherOpen(false)}
      />
    </>
  );
}
